/**
 * Pairwise Ranking tool runner.
 * Ranks items by pairwise comparisons using win/loss counting.
 * 
 * Note: This is a simplified implementation. In a real system, comparisons
 * would be collected through interactive UI. For this runner, we assume
 * comparison results are pre-computed and stored in item attributes.
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Pairwise Ranking.
 */
export function getPairwiseRankingSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("pairwise-ranking");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Pairwise Ranking tool.
 * 
 * Algorithm: Each item starts with 0 wins. For each pairwise comparison,
 * the winner gets +1 win. Final score = total wins. Rank = sorted by wins (descending).
 * 
 * Note: This implementation assumes comparison results are stored in item attributes.
 * In a real system, comparisons would be collected interactively.
 * 
 * @param items - Items to rank
 * @param _criteriaSet - CriteriaSet (not used by Pairwise Ranking)
 * @param toolConfig - Tool configuration (enable_tie_breaking, max_items, min_items)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runPairwiseRankingTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `pairwise-ranking-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const enableTieBreaking = (toolConfig.enable_tie_breaking as boolean) ?? true;
  const maxItems = (toolConfig.max_items as number) || 15;
  const minItems = (toolConfig.min_items as number) || 2;

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    // Use the arrange validation applyLens function
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Validate item count
  if (filteredItems.length < minItems) {
    throw new Error(
      `Pairwise Ranking requires at least ${minItems} items, got ${filteredItems.length}`
    );
  }
  if (filteredItems.length > maxItems) {
    throw new Error(
      `Pairwise Ranking supports at most ${maxItems} items, got ${filteredItems.length}`
    );
  }

  // Extract win counts from items
  // In a real implementation, wins would be computed from pairwise comparison results
  // For this runner, we look for wins/score in attributes
  const itemsWithWins: Array<{ item: Item; wins: number; losses: number; comparisons: number }> = [];

  for (const item of filteredItems) {
    // Look for wins in attributes (wins, score, or default to 0)
    const wins =
      item.attributes.wins !== undefined
        ? (typeof item.attributes.wins === "number" ? item.attributes.wins : Number(item.attributes.wins) || 0)
        : item.attributes.score !== undefined
        ? (typeof item.attributes.score === "number" ? item.attributes.score : Number(item.attributes.score) || 0)
        : 0;

    const losses =
      item.attributes.losses !== undefined
        ? (typeof item.attributes.losses === "number" ? item.attributes.losses : Number(item.attributes.losses) || 0)
        : 0;

    const comparisons = wins + losses;

    itemsWithWins.push({ item, wins, losses, comparisons });
  }

  // Calculate total comparisons (n*(n-1)/2)
  const n = filteredItems.length;
  const totalComparisons = (n * (n - 1)) / 2;

  // Sort by wins (descending), then by label (ascending) for tie-breaking
  itemsWithWins.sort((a, b) => {
    if (b.wins !== a.wins) {
      return b.wins - a.wins;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Assign ranks and build output
  const scoredItems: Array<{
    item_id: string;
    score: number;
    rank: number;
    wins: number;
    losses: number;
    comparisons: number;
    is_tied: boolean;
  }> = [];
  let currentRank = 1;
  
  for (let i = 0; i < itemsWithWins.length; i++) {
    const { item, wins, losses, comparisons } = itemsWithWins[i];
    
    // If wins differ from previous, update rank
    if (i > 0 && itemsWithWins[i - 1].wins !== wins) {
      currentRank = i + 1;
    }

    const isTied = i > 0 && itemsWithWins[i - 1].wins === wins;

    scoredItems.push({
      item_id: item.id,
      score: wins, // score = wins
      rank: currentRank,
      wins: wins,
      losses: losses,
      comparisons: comparisons,
      is_tied: isTied,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      total_comparisons: totalComparisons,
      enable_tie_breaking: enableTieBreaking,
    },
  };
}

